import type { ActionTree } from 'vuex';
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import type { DocumentStatus } from '@getaccept/lib-shared/src/enums/document-status';
import { alertNamespace, AlertAction } from '@getaccept/lib-shared/src/alert/store/alert.constants';
import type { RootState } from '../../store/root.types';
import { SessionService } from '../../api/session/services/session.service';
import type { AuthenticationStepInput } from '../../authentication/types/authentication-step-input';
import type { AuthenticationStep } from '../../authentication/types/authentication-step';
import { AuthenticationStepType } from '../../authentication/types/enums/authentication-step-type';
import type { Token } from '../type/token';
import { AuthenticationHelper } from '../../authentication/helpers/authentication.helper';
import { HttpThrottleError, HttpLockedError } from '../../common/errors';
import { SessionAction, SessionMutation } from './session.types';
import type { SessionState } from './session.types';

export const actions: ActionTree<SessionState, RootState> = {
  async [SessionAction.LoadToken](
    { dispatch, rootState },
    authStepsInput?: AuthenticationStepInput[]
  ) {
    try {
      const token: Token = await SessionService.getToken(
        rootState.documentId,
        rootState.recipientId,
        rootState.authId,
        authStepsInput
      );
      dispatch(SessionAction.LoadTokenSuccess, { token, authStepsInput });
    } catch (e) {
      dispatch(SessionAction.LoadTokenFailed, e);
    }
  },
  [SessionAction.LoadTokenSuccess](
    { commit, dispatch },
    {
      token,
      authStepsInput,
    }: {
      token: Token;
      authStepsInput: AuthenticationStepInput[];
    }
  ) {
    commit(SessionMutation.SetSubmittingResponse, false);
    if (token.authSteps?.length) {
      dispatch(SessionAction.SetAuthenticationSteps, token.authSteps);
      if (authStepsInput) {
        dispatch(SessionAction.SetAuthenticationAnswers, {
          authSteps: token.authSteps,
          authAnswers: authStepsInput,
        });
      }
    } else if (token.metaData && token.metaData.redirectUrl) {
      const pathRegExp = /\/([a-z,\d]+)\/([a-z,\d]+)\/a\/([a-z,\d]+)/g;
      const redirectUrl: string = window.location.href.replace(
        pathRegExp,
        token.metaData.redirectUrl
      );
      window.location.assign(`${redirectUrl}?token=${token.jwt}`);
    } else if (token.jwt) {
      commit(SessionMutation.SetToken, token);
      commit(SessionMutation.SetStatus, token.status);
      dispatch(SessionAction.SetAuthenticationSteps, []);
    } else if (token.status) {
      commit(SessionMutation.SetStatus, token.status);
      dispatch(SessionAction.SetAuthenticationSteps, []);
    } else {
      commit(SessionMutation.SetError, true);
    }
  },
  [SessionAction.LoadTokenFailed]({ commit }) {
    commit(SessionMutation.SetSubmittingResponse, false);
    commit(SessionMutation.SetError, true);
  },
  [SessionAction.SetAuthenticationSteps]({ commit }, steps: AuthenticationStep[]) {
    commit(SessionMutation.SetAuthenticationSteps, steps);
  },
  [SessionAction.SetAuthenticationAnswers](
    { commit },
    {
      authSteps,
      authAnswers,
    }: { authSteps: AuthenticationStep[]; authAnswers: AuthenticationStepInput[] }
  ) {
    const stepsWithError: AuthenticationStep[] = authSteps.filter(
      (step: AuthenticationStep) => step.data.error
    );
    const correctAnswers: AuthenticationStepInput[] = authAnswers.filter(
      (answer: AuthenticationStepInput) => {
        return !stepsWithError.some((step: AuthenticationStep) => step.type === answer.type);
      }
    );
    commit(SessionMutation.SetAuthenticationAnswers, correctAnswers);
  },
  [SessionAction.SubmitAnswer](
    { commit, dispatch, state },
    authStepsInput: AuthenticationStepInput[]
  ) {
    try {
      const existingAuthStepsInput: AuthenticationStepInput[] = [...state.authentication.answers];
      authStepsInput.forEach((authStepInput: AuthenticationStepInput) => {
        const alreadyHasAnswer: boolean = state.authentication.answers.some(
          (answer: AuthenticationStepInput) => answer.type === authStepInput.type
        );

        if (alreadyHasAnswer) {
          const index: number = state.authentication.answers.findIndex(
            (answer: AuthenticationStepInput) => answer.type === authStepInput.type
          );
          existingAuthStepsInput.splice(index, 1, authStepInput);
        } else {
          existingAuthStepsInput.push(authStepInput);
        }
      });
      dispatch(SessionAction.LoadToken, existingAuthStepsInput);
    } catch (e) {
      commit(SessionMutation.SetSubmittingResponse, false);
      console.error(e);
    }
  },
  async [SessionAction.SendSmsCode]({ commit, dispatch, state, rootState }) {
    try {
      await SessionService.postSendSmsLegacy(
        rootState.documentId,
        rootState.recipientId,
        rootState.authId,
        state.sendPinRetryCount
      );
      commit(SessionMutation.SetSendPinRetryCount, state.sendPinRetryCount + 1);
    } catch (e) {
      if (e instanceof HttpThrottleError) {
        dispatch(SessionAction.SendSmsCodeThrottleFailed);
      } else if (e instanceof HttpLockedError) {
        dispatch(SessionAction.SendSmsCodeLockedFailed);
      } else {
        dispatch(SessionAction.SendSmsCodeFailed);
      }
    }
  },
  [SessionAction.SendSmsCodeFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/${AlertAction.ShowDanger}`, t('could-not-send-sms-code'), {
      root: true,
    });
  },
  [SessionAction.SendSmsCodeThrottleFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/${AlertAction.ShowDanger}`, t('could-not-send-sms-code-throttle'), {
      root: true,
    });
  },
  [SessionAction.SendSmsCodeLockedFailed]({ dispatch }) {
    dispatch(`${alertNamespace}/${AlertAction.ShowDanger}`, t('could-not-send-sms-code-locked'), {
      root: true,
    });
  },
  async [SessionAction.SubmitSmsAnswer](
    { commit, dispatch, rootState },
    authStepsInput: AuthenticationStepInput
  ) {
    commit(SessionMutation.SetSubmittingResponse, true);
    try {
      const verified: boolean = await SessionService.postVerifySmsLegacy(
        authStepsInput,
        rootState.documentId,
        rootState.recipientId,
        rootState.authId
      );
      if (verified) {
        dispatch(SessionAction.SubmitAnswer, [authStepsInput]);
      } else {
        dispatch(SessionAction.SubmitSmsAnswerFailed);
      }
    } catch (e) {
      dispatch(SessionAction.SubmitSmsAnswerFailed);
    }
  },
  [SessionAction.SubmitSmsAnswerFailed]({ commit }) {
    commit(SessionMutation.SetSubmittingResponse, false);
    commit(SessionMutation.VerifySms, false);
  },
  async [SessionAction.SubmitQnaAnswer](
    { commit, dispatch, rootState },
    authStep: AuthenticationStepInput
  ) {
    commit(SessionMutation.SetSubmittingResponse, true);
    try {
      await SessionService.postQAAnswerLegacy(
        authStep,
        rootState.documentId,
        rootState.recipientId,
        rootState.authId
      );
      dispatch(SessionAction.SubmitAnswer, [authStep]);
    } catch (e) {
      dispatch(SessionAction.SubmitQnaAnswerFailed);
    }
  },
  [SessionAction.SubmitQnaAnswerFailed]({ commit, dispatch }) {
    commit(SessionMutation.SetSubmittingResponse, false);
    dispatch(`${alertNamespace}/${AlertAction.ShowDanger}`, t('could-not-save-qna-answer'), {
      root: true,
    });
  },
  async [SessionAction.SubmitConsent]({ dispatch }, authStepsInput: AuthenticationStepInput[]) {
    for (let i = 0; i < authStepsInput.length; i++) {
      switch (authStepsInput[i].type) {
        case AuthenticationStepType.GdprConsent:
          await dispatch(SessionAction.HandleGdprConsent, authStepsInput[i]);
          break;
        case AuthenticationStepType.TrackingConsent:
          await dispatch(SessionAction.HandleTrackingConsent, authStepsInput[i]);
          break;
      }
    }
    dispatch(SessionAction.SubmitAnswer, authStepsInput);
  },
  [SessionAction.DeclineConsent]({ dispatch, state }) {
    const declinedGDPRConsent = AuthenticationHelper.declinedConsent(
      state.authentication.steps,
      AuthenticationStepType.GdprConsent
    );
    const declinedTrackingConsent = AuthenticationHelper.declinedConsent(
      state.authentication.steps,
      AuthenticationStepType.TrackingConsent
    );
    const authStepsInput: AuthenticationStepInput[] = [];
    if (declinedGDPRConsent) {
      authStepsInput.push({ value: '0', type: AuthenticationStepType.GdprConsent });
    }
    if (declinedTrackingConsent) {
      authStepsInput.push({ value: '0', type: AuthenticationStepType.TrackingConsent });
    }
    dispatch(SessionAction.SubmitConsent, authStepsInput);
  },
  async [SessionAction.HandleGdprConsent](
    { commit, dispatch, rootState },
    authStepInput: AuthenticationStepInput
  ) {
    commit(SessionMutation.SetSubmittingResponse, true);
    try {
      if (authStepInput.value === '1') {
        await SessionService.postVerifyConsentLegacy(
          rootState.documentId,
          rootState.recipientId,
          rootState.authId
        );
      } else {
        await SessionService.postRejectConsentLegacy(
          rootState.documentId,
          rootState.recipientId,
          rootState.authId
        );
      }
      commit(SessionMutation.SetSubmittingResponse, false);
    } catch (error) {
      console.log(error);
      dispatch(SessionAction.SubmitConsentFailed, error);
    }
  },
  async [SessionAction.HandleTrackingConsent](
    { dispatch, commit, rootState },
    authStepInput: AuthenticationStepInput
  ) {
    commit(SessionMutation.SetSubmittingResponse, true);
    try {
      if (authStepInput.value === '1') {
        await SessionService.postVerifyTrackingConsentLegacy(
          rootState.documentId,
          rootState.recipientId,
          rootState.authId
        );
      } else {
        await SessionService.postRejectTrackingConsentLegacy(
          rootState.documentId,
          rootState.recipientId,
          rootState.authId
        );
      }
      commit(SessionMutation.SetSubmittingResponse, true);
    } catch (error) {
      dispatch(SessionAction.SubmitConsentFailed);
    }
  },
  [SessionAction.SubmitConsentFailed]({ commit, dispatch }) {
    commit(SessionMutation.SetSubmittingResponse, false);
    dispatch(`${alertNamespace}/${AlertAction.ShowDanger}`, t('could-not-save-consent'), {
      root: true,
    });
  },
  [SessionAction.SetStatus]({ commit }, status: DocumentStatus) {
    commit(SessionMutation.SetStatus, status);
  },
  [SessionAction.SetPreviewToken]({ commit }, token: string) {
    commit(SessionMutation.SetToken, { jwt: token });
  },
  [SessionAction.SetRecipientFirstOpen]({ commit }, value: boolean) {
    commit(SessionMutation.SetRecipientFirstOpen, value);
  },
};
